import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PHASES_OF_PLAY, PHASE_OF_PLAY_META } from '@4cm/core/phaseOfPlay';
import type { PhaseOfPlay } from '@4cm/core/phaseOfPlay';
import { BrainOrb, Card, EmptyState, GlassPill, Skeleton } from '../components/primitives';
import { useActiveTeam } from '../lib/queries/useActiveTeam';
import { useTeamSessions } from '../lib/queries/useTeamSessions';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import './page-shell.css';
import './Sessions.css';

function topPhase(coverage: Record<string, number> | null): PhaseOfPlay | null {
  if (!coverage) return null;
  let best: PhaseOfPlay | null = null;
  let bestValue = 0;
  for (const phase of PHASES_OF_PLAY) {
    const value = Number(coverage[phase] ?? 0);
    if (value > bestValue) { best = phase; bestValue = value; }
  }
  return best;
}

function formatRelative(iso: string): string {
  const date = new Date(iso);
  const days = Math.round((date.getTime() - Date.now()) / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  if (days > 0 && days < 7) return `in ${days}d`;
  if (days < 0 && days > -14) return `${Math.abs(days)}d ago`;
  return date.toLocaleDateString();
}

export function Sessions() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [creating, setCreating] = useState(false);
  // Copy in flight (per-card spinner) + optimistic soft-delete hiding.
  const [copyingId, setCopyingId] = useState<string | null>(null);
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { team, loading: teamLoading } = useActiveTeam();
  const { rows: sessions, state, errorMessage } = useTeamSessions(team?.id, teamLoading, {
    order: 'desc',
    limit: 40,
  });

  // "+ New session" — create an empty session in DB + jump to its detail
  // page where the coach has BOTH "+ Add drill from library" and
  // "Ask Brain for a drill". Mirrors the "+ New drill" pattern. The
  // existing /sessions/new (Brain-driven composer) is still reachable
  // via the "Plan with Brain" button.
  const createBlankSession = async () => {
    if (!user || !team?.id || creating) return;
    setCreating(true);
    const today = new Date();
    const iso = today.toISOString().slice(0, 10);
    const { data, error } = await supabase
      .from('sessions')
      .insert({
        team_id: team.id,
        coach_id: user.id,
        date: iso,
        title: 'New session',
        objectives: [],
        corner_coverage: { technical_tactical: 45, physical: 20, psychological: 20, social: 15 },
        psych_social_intent: '',
        duration_mins: 60,
      })
      .select('id')
      .single();
    setCreating(false);
    if (error || !data) {
      alert(`Couldn't create session: ${error?.message ?? 'unknown error'}`);
      return;
    }
    navigate(`/sessions/${(data as { id: string }).id}`);
  };

  // Copy: deep-copy the session row + its drill links into a new DRAFT, then
  // open the copy. Strips server-managed + share columns so the unique
  // share_token doesn't clash.
  const handleCopy = async (s: { id: string; title: string | null }) => {
    if (!user || !team?.id || copyingId) return;
    setCopyingId(s.id);
    try {
      const { data: src, error: fetchErr } = await supabase
        .from('sessions').select('*').eq('id', s.id).maybeSingle();
      if (fetchErr || !src) {
        alert(`Couldn't copy session: ${fetchErr?.message ?? 'session not found'}`);
        return;
      }
      const content: Record<string, unknown> = { ...(src as Record<string, unknown>) };
      for (const k of ['id', 'created_at', 'updated_at', 'deleted_at', 'share_token', 'share_enabled']) {
        delete content[k];
      }
      const { data: inserted, error: insErr } = await supabase
        .from('sessions')
        .insert({ ...content, title: `${s.title ?? 'Session'} (copy)`, status: 'DRAFT' })
        .select('id')
        .single();
      if (insErr || !inserted) {
        alert(`Couldn't copy session: ${insErr?.message ?? 'unknown error'}`);
        return;
      }
      const newId = (inserted as { id: string }).id;
      // Copy the drill composition (same drills, same order).
      const { data: links } = await supabase
        .from('session_drills').select('drill_id, sort_order').eq('session_id', s.id);
      const rows = ((links ?? []) as Array<{ drill_id: string; sort_order: number }>)
        .map((l) => ({ session_id: newId, drill_id: l.drill_id, sort_order: l.sort_order }));
      if (rows.length > 0) await supabase.from('session_drills').insert(rows);
      navigate(`/sessions/${newId}`);
    } finally {
      setCopyingId(null);
    }
  };

  return (
    <main className="main page sessions-page">
      <header className="page-head sessions-head">
        <BrainOrb size="lg" ariaLabel="Brain" />
        <div style={{ flex: 1 }}>
          <span className="page-eyebrow">Layer 2 · planning</span>
          <h1 className="page-title">Sessions</h1>
          <p className="page-sub">Last fortnight + the next two weeks.</p>
        </div>
        {team?.id && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => navigate('/sessions/new')}
              style={{
                padding: '8px 14px',
                background: 'transparent',
                color: 'inherit',
                border: '1px solid rgba(255,255,255,0.25)',
                borderRadius: 6,
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
              }}
            >
              ✨ Plan with Brain
            </button>
            <button
              type="button"
              disabled={creating}
              onClick={() => void createBlankSession()}
              style={{
                padding: '8px 14px',
                background: 'var(--brand, #2980B9)',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontWeight: 600,
                fontSize: 14,
                cursor: creating ? 'wait' : 'pointer',
              }}
            >
              {creating ? 'Creating…' : '+ New session'}
            </button>
          </div>
        )}
      </header>

      {state === 'loading' && (
        <ul className="session-list" aria-label="Loading sessions">
          {Array.from({ length: 4 }, (_, i) => (
            <li key={i}>
              <Card variant="opaque">
                <Skeleton variant="text" width="40%" height={18} />
                <div style={{ height: 8 }} />
                <Skeleton variant="text" width="70%" height={14} />
                <div style={{ height: 8 }} />
                <Skeleton variant="pill" />
              </Card>
            </li>
          ))}
        </ul>
      )}
      {state === 'error' && (
        <Card variant="opaque">
          <p className="page-empty">
            Couldn't load sessions.{errorMessage ? ` (${errorMessage})` : null}
          </p>
        </Card>
      )}
      {state === 'empty' && (
        <EmptyState
          glyph="⚽"
          title={team?.id ? 'No sessions in the window' : 'No team yet'}
          body={team?.id
            ? 'Last fortnight + the next two weeks are clear. Plan tonight from Today, or hit ⌘K and ask Brain.'
            : 'Onboarding takes about 5 minutes — voice + a few taps and you\'ll have a usable training week.'}
          cta={{
            label: team?.id ? 'Plan a session' : 'Start onboarding',
            onClick: () => navigate(team?.id ? '/sessions/new' : '/onboarding'),
          }}
          hint={team?.id ? <>or press <kbd>⌘K</kbd> and ask Brain</> : null}
        />
      )}
      {state === 'ok' && sessions.some((s) => !deletedIds.has(s.id)) && (
        <ul className="session-list">
          {sessions.filter((s) => !deletedIds.has(s.id)).map((s) => {
            const phase = topPhase(s.phase_coverage);
            return (
              <li key={s.id} data-testid="session-card" data-session-id={s.id}>
                <Link to={`/sessions/${s.id}`} className="session-card">
                  <Card variant="opaque">
                    <div className="session-card__row">
                      <div className="session-card__main">
                        <div className="session-card__title">{s.title ?? 'Untitled session'}</div>
                        <div className="session-card__meta">
                          <span>{formatRelative(s.date)}</span>
                          <span className="session-card__sep">·</span>
                          <span>{new Date(s.date).toLocaleDateString()}</span>
                          {s.duration_mins && (
                            <>
                              <span className="session-card__sep">·</span>
                              <span>{s.duration_mins} min</span>
                            </>
                          )}
                        </div>
                      </div>
                      {phase && (
                        <GlassPill mono>
                          <span aria-hidden="true">{PHASE_OF_PLAY_META[phase].glyph}</span>
                          {PHASE_OF_PLAY_META[phase].label}
                        </GlassPill>
                      )}
                    </div>
                    {user && team?.id && (
                      <div className="session-card__actions" style={{ display: 'flex', gap: 14, marginTop: 10, alignItems: 'center' }}>
                        <button
                          type="button"
                          data-testid="session-copy"
                          disabled={copyingId === s.id}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); void handleCopy(s); }}
                          title="Copy this session into a new draft"
                          style={{ background: 'transparent', border: 'none', color: 'inherit', font: 'inherit', cursor: copyingId === s.id ? 'wait' : 'pointer', padding: 0, fontSize: 13, opacity: copyingId === s.id ? 0.6 : 1 }}
                        >
                          {copyingId === s.id ? 'Copying…' : '⧉ Copy'}
                        </button>
                        <button
                          type="button"
                          data-testid="session-delete"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeleteTarget({ id: s.id, title: s.title ?? 'this session' }); }}
                          title="Delete this session"
                          style={{ background: 'transparent', border: 'none', color: 'rgba(255, 100, 100, 0.85)', font: 'inherit', cursor: 'pointer', padding: 0, fontSize: 13 }}
                        >
                          🗑 Delete
                        </button>
                      </div>
                    )}
                  </Card>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {deleteTarget && (
        <div
          role="dialog"
          aria-label="Delete session"
          style={{
            position: 'fixed', inset: 0, zIndex: 1100,
            background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => !deleting && setDeleteTarget(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(420px, calc(100vw - 32px))',
              background: 'rgba(15, 25, 45, 0.97)',
              border: '1px solid rgba(255,255,255,0.18)',
              borderRadius: 12, padding: 20, color: '#fff',
              fontFamily: 'system-ui, sans-serif',
              display: 'flex', flexDirection: 'column', gap: 12,
            }}
          >
            <h2 style={{ margin: 0, fontSize: 16 }}>Delete this session?</h2>
            <p style={{ margin: 0, fontSize: 13, opacity: 0.85, lineHeight: 1.5 }}>
              <strong>{deleteTarget.title}</strong> will be removed from your sessions. It&apos;s soft-deleted, so it can be recovered from the database if needed.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                disabled={deleting}
                onClick={() => setDeleteTarget(null)}
                style={{ padding: '8px 14px', background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6, fontSize: 13, cursor: deleting ? 'wait' : 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={deleting}
                onClick={async () => {
                  setDeleting(true);
                  const { error } = await supabase
                    .from('sessions')
                    .update({ deleted_at: new Date().toISOString() })
                    .eq('id', deleteTarget.id);
                  setDeleting(false);
                  if (error) {
                    alert(`Couldn't delete session: ${error.message}`);
                    return;
                  }
                  setDeletedIds((prev) => {
                    const next = new Set(prev);
                    next.add(deleteTarget.id);
                    return next;
                  });
                  setDeleteTarget(null);
                }}
                style={{ padding: '8px 14px', background: 'var(--state-bad)', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: deleting ? 'wait' : 'pointer' }}
              >
                {deleting ? 'Deleting…' : 'Delete session'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
